// comments-plugin.ts
import { Plugin, PluginKey, Transaction, EditorState } from "prosemirror-state";
import { Editor } from "@tiptap/react";
import { Comment } from "../use-collaborative";
import { Decoration, DecorationSet } from "prosemirror-view";
import * as Y from "yjs";

type CommentState = {
  deco: DecorationSet;
  comments: Comment[];
  activeId: string | null;
};

export const commentPluginKey = new PluginKey<CommentState>("commentHighlight");

export const COMMENTS_META = "comments:update";

export const createCommentPlugin = (ydoc: Y.Doc) => {
  return new Plugin<CommentState>({
    key: commentPluginKey,
    state: {
      init(): CommentState {
        return { deco: DecorationSet.empty, comments: [], activeId: null };
      },
      apply(
        tr: Transaction,
        prev: CommentState,
        _oldState: EditorState,
        newState: EditorState
      ): CommentState {
        // 1) map existing decorations through the transaction
        let deco = prev.deco.map(tr.mapping, tr.doc);
        let comments = prev.comments;
        let activeId = prev.activeId;

        // 2) React pushed updates via meta?
        const meta = tr.getMeta(COMMENTS_META) as
          | { comments?: Comment[]; activeId?: string | null }
          | undefined;

        if (meta) {
          if (meta.comments) comments = meta.comments;
          if (meta.activeId !== undefined) activeId = meta.activeId;
          deco = buildDecorations(newState, ydoc, comments, activeId);
        }

        return { deco, comments, activeId };
      }
    },
    props: {
      decorations(state) {
        return commentPluginKey.getState(state)?.deco ?? DecorationSet.empty;
      }
    }
  });
};

const buildDecorations = (
  state: EditorState,
  ydoc: Y.Doc,
  comments: Comment[],
  activeId: string | null
): DecorationSet => {
  const decos: Decoration[] = [];

  (comments ?? [])
    .filter((c) => !c.parentComment && !c.isOrphaned)
    .forEach((c) => {
      let fromAbs = null;
      let toAbs = null;

      const yCommentsRef = ydoc.getMap("commentsRef");

      const commRef = yCommentsRef.get(c.id) as Y.Map<any>;

      fromAbs = commRef?.get("from");
      toAbs = commRef?.get("to");

      if (fromAbs && toAbs && fromAbs !== toAbs) {
        decos.push(
          Decoration.inline(fromAbs, toAbs, {
            class: c.id === activeId ? "highlight-active" : "comment-highlight"
          })
        );
      }
    });

  return DecorationSet.create(state.doc, decos);
};

export const setEditorComments = (editor: Editor, comments: Comment[]) => {
  const transaction = editor.state.tr.setMeta(COMMENTS_META, { comments });
  editor.view.dispatch(transaction);
};

export const setEditorActiveComment = (editor: Editor, active: Comment | null) => {
  const transaction = editor.state.tr.setMeta(COMMENTS_META, {
    activeId: active?.id ?? null
  });

  editor.view.dispatch(transaction);
};
